import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { SearchableSelect } from "@/components/SearchableSelect";
import { formatMetric } from "@/lib/format";
import type { LastFlightDetail } from "@/types/api";
import type { WidgetComponentProps } from "../types";
import { useSummaryQuery } from "../useSummaryQuery";

const MPS_TO_KMH = 3.6;

function formatWhen(start: string | null): string {
  if (!start) return "—";
  try {
    return new Date(start).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return start;
  }
}

function StatusPill({ flight }: { flight: LastFlightDetail }) {
  return (
    <span className={`status-pill status-pill--${flight.status}`}>
      {flight.status_label}
    </span>
  );
}

function LastFlightSmall({ flight }: { flight: LastFlightDetail }) {
  return (
    <div className="last-flight last-flight--s">
      <div className="last-flight__row">
        <p className="eyebrow">Last flight</p>
        <StatusPill flight={flight} />
      </div>
      <h3 className="last-flight__title">
        {flight.session_name || `Flight ${flight.id}`}
      </h3>
      <div className="last-flight__pair">
        <div className="last-flight__pair-item">
          <span className="last-flight__pair-lbl">Duration</span>
          <span className="last-flight__pair-val">{flight.duration_display}</span>
        </div>
        <div className="last-flight__pair-item">
          <span className="last-flight__pair-lbl">Distance</span>
          <span className="last-flight__pair-val">{flight.distance_display}</span>
        </div>
      </div>
      <Link className="text-link last-flight__link" to={`/flights/${flight.id}`}>
        Open →
      </Link>
    </div>
  );
}

function LastFlightMedium({ flight }: { flight: LastFlightDetail }) {
  const stats = [
    { label: "Duration", value: flight.duration_display },
    { label: "Distance", value: flight.distance_display },
    { label: "Max alt", value: formatMetric(flight.max_altitude_m ?? null, 0, " m") },
    {
      label: "Avg speed",
      value:
        flight.avg_speed_mps != null
          ? formatMetric(flight.avg_speed_mps * MPS_TO_KMH, 1, " km/h")
          : "—",
    },
  ];
  return (
    <div className="last-flight last-flight--m">
      <div className="last-flight__head">
        <div className="last-flight__head-text">
          <p className="eyebrow">Last flight · {formatWhen(flight.start_time)}</p>
          <h3 className="last-flight__title">
            {flight.session_name || `Flight ${flight.id}`}
          </h3>
          <p className="last-flight__meta">
            {[flight.mission_name, flight.map_name, flight.owner_label]
              .filter(Boolean)
              .join(" · ") || "—"}
          </p>
        </div>
        <StatusPill flight={flight} />
      </div>
      <div className="last-flight__stats-grid">
        {stats.map((s) => (
          <div key={s.label} className="last-flight__stat">
            <span className="last-flight__stat-lbl">{s.label}</span>
            <span className="last-flight__stat-val">{s.value}</span>
          </div>
        ))}
      </div>
      <Link className="text-link last-flight__link" to={`/flights/${flight.id}`}>
        Open replay →
      </Link>
    </div>
  );
}

function LastFlightLarge({ flight }: { flight: LastFlightDetail }) {
  const maxKmh = flight.max_speed_mps != null ? flight.max_speed_mps * MPS_TO_KMH : null;
  const avgKmh = flight.avg_speed_mps != null ? flight.avg_speed_mps * MPS_TO_KMH : null;
  return (
    <div className="last-flight last-flight--l">
      <div className="last-flight__head">
        <div className="last-flight__head-text">
          <p className="eyebrow">Last flight · {formatWhen(flight.start_time)}</p>
          <h3 className="last-flight__title">
            {flight.session_name || `Flight ${flight.id}`}
          </h3>
          <p className="last-flight__meta">
            {[flight.mission_name, flight.map_name, flight.owner_label]
              .filter(Boolean)
              .join(" · ") || "—"}
          </p>
        </div>
        <StatusPill flight={flight} />
      </div>

      <div className="last-flight__grid-large">
        <div className="last-flight__stats-grid last-flight__stats-grid--dense">
          <div className="last-flight__stat">
            <span className="last-flight__stat-lbl">Duration</span>
            <span className="last-flight__stat-val">{flight.duration_display}</span>
          </div>
          <div className="last-flight__stat">
            <span className="last-flight__stat-lbl">Distance</span>
            <span className="last-flight__stat-val">{flight.distance_display}</span>
          </div>
          <div className="last-flight__stat">
            <span className="last-flight__stat-lbl">Max alt</span>
            <span className="last-flight__stat-val">
              {formatMetric(flight.max_altitude_m ?? null, 0, " m")}
            </span>
          </div>
          <div className="last-flight__stat">
            <span className="last-flight__stat-lbl">Avg speed</span>
            <span className="last-flight__stat-val">
              {avgKmh != null ? formatMetric(avgKmh, 1, " km/h") : "—"}
            </span>
          </div>
          <div className="last-flight__stat">
            <span className="last-flight__stat-lbl">Peak speed</span>
            <span className="last-flight__stat-val">
              {maxKmh != null ? formatMetric(maxKmh, 1, " km/h") : "—"}
            </span>
          </div>
          <div className="last-flight__stat">
            <span className="last-flight__stat-lbl">Smoothness</span>
            <span className="last-flight__stat-val">
              {flight.smoothness_score != null
                ? `${Math.round(flight.smoothness_score)}/100`
                : "—"}
            </span>
          </div>
        </div>

        <div className="last-flight__side">
          {flight.smoothness_score != null && (
            <div className="last-flight__smooth">
              <div className="last-flight__smooth-head">
                <span className="eyebrow">Smoothness</span>
                <span>{Math.round(flight.smoothness_score)}/100</span>
              </div>
              <div className="last-flight__smooth-bar">
                <div
                  className="last-flight__smooth-fill"
                  style={{
                    width: `${Math.max(0, Math.min(100, flight.smoothness_score))}%`,
                  }}
                />
              </div>
            </div>
          )}
          {flight.ai_feedback_summary && (
            <div className="last-flight__ai">
              <p className="eyebrow">AI feedback</p>
              <p className="last-flight__ai-text">{flight.ai_feedback_summary}</p>
            </div>
          )}
        </div>
      </div>

      <div className="last-flight__footer">
        <Link className="text-link" to={`/flights/${flight.id}`}>
          Open replay →
        </Link>
        <Link className="text-link" to={`/analysis?flight_id=${flight.id}`}>
          Deep analysis →
        </Link>
      </div>
    </div>
  );
}

interface FlightOption {
  id: number;
  label: string;
}

function FlightPicker({
  options,
  value,
  onChange,
}: {
  options: FlightOption[];
  value: number | null;
  onChange: (id: number | null) => void;
}) {
  if (options.length <= 1) return null;
  const selectOptions = [
    { value: "", label: "Latest" },
    ...options.map((opt) => ({ value: String(opt.id), label: opt.label })),
  ];
  return (
    <div className="flight-picker">
      <span className="flight-picker__label">Flight</span>
      <SearchableSelect
        className="flight-picker__select"
        value={value == null ? "" : String(value)}
        onChange={(v) => onChange(v === "" ? null : Number(v))}
        options={selectOptions}
        size="minimal"
        minWidth={200}
        maxWidth={300}
        searchPlaceholder="Search flight…"
        ariaLabel="Select flight"
        searchable={options.length > 6}
      />
    </div>
  );
}

export function LastFlightWidget({ sizeVariant }: WidgetComponentProps) {
  // `null` = follow the newest flight; otherwise pin to a specific flight id.
  const [pinnedFlightId, setPinnedFlightId] = useState<number | null>(null);
  // Always fetch the base summary so the picker has a list of recent flights,
  // and fetch the selected detail separately when pinned.
  const baseQuery = useSummaryQuery();
  const pinnedQuery = useSummaryQuery(pinnedFlightId);

  const flight = pinnedFlightId != null ? pinnedQuery.data?.last_flight : baseQuery.data?.last_flight;
  const isLoading = pinnedFlightId != null ? pinnedQuery.isLoading : baseQuery.isLoading;
  const isError = pinnedFlightId != null ? pinnedQuery.isError : baseQuery.isError;

  const flightOptions = useMemo<FlightOption[]>(() => {
    const rows = baseQuery.data?.recent_flights ?? [];
    return rows.map((row) => ({
      id: row.id,
      label: `${row.session_name || `Flight ${row.id}`} · ${row.date_display}`,
    }));
  }, [baseQuery.data?.recent_flights]);

  const showPicker = sizeVariant !== "xs" && sizeVariant !== "s";

  const picker = showPicker ? (
    <FlightPicker
      options={flightOptions}
      value={pinnedFlightId}
      onChange={setPinnedFlightId}
    />
  ) : null;

  if (isLoading) return <p className="widget-empty">Loading…</p>;
  if (isError) return <p className="widget-empty">Failed to load.</p>;
  if (!flight) {
    return <p className="widget-empty">No flights yet. Upload a replay to begin.</p>;
  }

  if (sizeVariant === "xs" || sizeVariant === "s")
    return <LastFlightSmall flight={flight} />;
  if (sizeVariant === "l")
    return (
      <>
        {picker}
        <LastFlightLarge flight={flight} />
      </>
    );
  return (
    <>
      {picker}
      <LastFlightMedium flight={flight} />
    </>
  );
}
